import {
  legacyExtraWaitPrice,
  type LegacyCourierTracking,
  type LegacyOrderDetail,
  type LegacyOrderLeg,
  type LegacyPoint,
} from '@/entities/legacy-order';
import type { LegacyRoutePath } from '@/entities/legacy-order-draft';

/**
 * Pure derivations for the order-detail drawer, extracted so the arithmetic
 * can be tested without rendering a map.
 *
 * Every formula here is transcribed from the old panel's
 * `pages/delivery/single/` components. Where the source's behaviour looks like
 * a bug it is reproduced anyway (and flagged) — the point is a faithful port,
 * not a better calculator.
 */

/** A route point with which side of the trip it belongs to. */
export interface LegacyRoutePoint extends LegacyOrderLeg {
  type: 'origin' | 'destination';
}

/**
 * Origins first, then destinations — the order the address list and the
 * products list both iterate, and the basis of the running 1..n badge index.
 */
export function toRoutePoints(order: LegacyOrderDetail): LegacyRoutePoint[] {
  return [
    ...order.origin.map((leg) => ({ ...leg, type: 'origin' as const })),
    ...order.destination.map((leg) => ({ ...leg, type: 'destination' as const })),
  ];
}

/** Points that carry cargo — the only ones the products card lists. */
export function pointsWithItems(order: LegacyOrderDetail): LegacyRoutePoint[] {
  return toRoutePoints(order).filter((p) => (p.products?.items?.length ?? 0) > 0);
}

/**
 * `HH:mm`, or `-` when the timestamp is absent or unparseable. The source
 * renders `dayjs(x).isValid() ? format('HH:mm') : '-'` in a dozen places.
 */
export function legacyTimeOrDash(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** True when the courier has reached this point — gates two of its four stats. */
export function hasArrived(point: LegacyOrderLeg): boolean {
  if (!point.arrived_at) return false;
  return !Number.isNaN(new Date(point.arrived_at).getTime());
}

/**
 * The item's dimensions, exactly as the source concatenates them:
 * `10x 20x30` — note the asymmetric space after the FIRST separator only.
 * All three absent renders `-`.
 */
export function formatDimensions(item: {
  height?: number | null;
  width?: number | null;
  length?: number | null;
}): string {
  const { height, width, length } = item;
  if (!height && !width && !length) return '-';
  return [
    height ?? '',
    height ? 'x ' : '',
    width ?? '',
    width ? 'x' : '',
    length ?? '',
  ].join('');
}

/**
 * Grams → kilograms. The source divides by 1000 with NO rounding and NO unit
 * suffix, so 1500g shows as `1.5`.
 */
export function formatWeight(weight: number | null | undefined): string {
  if (!weight) return '-';
  return String(weight / 1000);
}

/**
 * Extra wait charge accrued past the free window.
 *
 * A thin adapter over the entity's shared `legacyExtraWaitPrice` (the orders
 * table's waiting column runs the same math): stage-8 + scooter only, whole
 * minutes past the free window, 500 so'm/min when the rate is absent or
 * negative.
 */
export function extraWaitPrice(order: LegacyOrderDetail, now: Date = new Date()): number {
  return legacyExtraWaitPrice(
    {
      stage: order.stage,
      equipmentId: order.delivery.equipment_id,
      freeWaitTime: order.free_wait_time,
      pricePerMinuteAfterWait: order.price_per_minute_after_wait,
      origin: order.origin,
    },
    now,
  );
}

/**
 * The five figures the price cards show.
 *
 * Two asymmetries are the SOURCE's, reproduced deliberately:
 * `total` adds the live extra wait charge, but `original` (delivery price)
 * subtracts only the stored `wait_price` and ignores `extraPrice` — so the
 * two cards can disagree by the accruing amount while a courier waits.
 */
export function priceBreakdown(
  order: LegacyOrderDetail,
  extraPrice: number,
): {
  total: number;
  wait: number;
  original: number;
  products: number;
  discount: number;
} {
  const { pricing } = order;
  return {
    total: pricing.total + extraPrice,
    wait: pricing.wait_price + extraPrice,
    // Deliberately NOT minus extraPrice — see above.
    original: pricing.total - pricing.wait_price,
    products: pricing.products,
    discount: pricing.discount ?? 0,
  };
}

/**
 * Stages at which an order may still be amended.
 *
 * The source's rule verbatim (`OrderDrawer.tsx:240-243`): up to and including
 * 13, but never 5 or 6. Those two are the moments the courier is being matched
 * — editing there would re-price an order mid-assignment.
 */
export function isEditableStage(stage: number): boolean {
  return stage <= 13 && stage !== 5 && stage !== 6;
}

/**
 * The stop the courier is headed to — the first leg not yet fully cleared.
 *
 * The source's predicate is `!arrived_at || !handed_over_at` (either marker
 * missing), NOT "not arrived": a leg the courier reached but has not handed
 * over at still counts as the next point. Reproduced verbatim.
 */
export function legacyNextPoint(points: LegacyRoutePoint[]): LegacyPoint | null {
  return points.find((p) => !p.arrived_at || !p.handed_over_at)?.location ?? null;
}

/**
 * The courier fix the map draws. The live tracking poll wins while it carries
 * a fix; otherwise the draw falls back to the order's own `courier.location`.
 * The poll is the drawer's ONLY courier source, so when it errors, is still
 * loading, or answers without a usable `location`, the marker (and the
 * courier-first route) silently vanish even though the order response already
 * carries a reported fix. The fallback has no dispatcher target, so no target
 * placemark is drawn for it.
 *
 * A 0/0 fix is "not set", not null island — the same rule the map's `placed`
 * filter applies to stops. That rule covers BOTH sources: a live poll
 * answering 0/0 ("no fix yet") must not shadow a valid order fix with a pin
 * at null island, so it falls through to the fallback like a missing fix.
 */
export function effectiveCourierTracking(
  tracking: LegacyCourierTracking | null,
  courierLocation: LegacyPoint | null,
): LegacyCourierTracking | null {
  if (tracking?.location?.lat && tracking.location.long) return tracking;
  if (!courierLocation?.lat || !courierLocation.long) return null;
  return { location: courierLocation, target_destination: null };
}

/**
 * The route to draw while a courier is assigned — the source's `customPoints`:
 * courier → dispatcher target → next stop when the dispatcher is sending the
 * courier somewhere OTHER than the order's own next stop, courier → next stop
 * otherwise, and `null` (fall back to the plain stop list) without a courier
 * fix.
 *
 * The "differs" test is the source's own quirk kept intact: BOTH coordinates
 * must differ (`lat !== lat && long !== long`), so a target sharing either
 * axis with the next stop reads as "same place".
 */
export function legacyCourierRoutePoints({
  location,
  target,
  nextPoint,
}: {
  location: LegacyPoint | null;
  target: LegacyPoint | null;
  nextPoint: LegacyPoint | null;
}): LegacyPoint[] | null {
  if (location?.lat == null || location.long == null) return null;

  if (
    nextPoint &&
    target?.lat &&
    target.long &&
    nextPoint.lat !== target.lat &&
    nextPoint.long !== target.long
  ) {
    return [location, target, nextPoint];
  }

  if (nextPoint) return [location, nextPoint];

  return null;
}

/**
 * Whether the dispatcher's target deserves its own placemark — only when it
 * exists and differs (same both-axes quirk as above) from the order's next
 * stop.
 */
export function legacyShowsTargetPlacemark({
  target,
  nextPoint,
}: {
  target: LegacyPoint | null;
  nextPoint: LegacyPoint | null;
}): boolean {
  if (!target?.lat || !target.long) return false;
  return nextPoint?.lat !== target.lat && nextPoint?.long !== target.long;
}

/**
 * Nearest entry of `candidates` to `point` — the source's `findNearest`,
 * QUIRK INCLUDED: it is called with the courier as `[lat, long]` against a
 * GeoJSON path whose entries are `[long, lat]`, so the metric mixes axes.
 * Deterministic, and reproduced rather than corrected — "fixing" it would
 * change when the route refetches.
 */
export function legacyFindNearest(
  point: [number, number],
  candidates: LegacyRoutePath,
): [number, number] | null {
  let nearest: [number, number] | null = null;
  let minDist = Infinity;

  for (const p of candidates) {
    const dist = Math.sqrt((point[0] - p[0]) ** 2 + (point[1] - p[1]) ** 2);
    if (dist < minDist) {
      minDist = dist;
      nearest = p;
    }
  }

  return nearest;
}

/** Great-circle distance in metres — the source's haversine verbatim. */
export function legacyHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dphi = toRad(lat2 - lat1);
  const dlambda = toRad(lon2 - lon1);

  const a =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dlambda / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * True when the courier has drifted 100 m or more off the drawn polyline —
 * the source's refetch trigger, axis quirk and all: the nearest point is
 * picked with mixed axes (see `legacyFindNearest`), then the real distance to
 * it is measured with the coordinates read correctly.
 */
export function legacyIsOffRoute(location: LegacyPoint, path: LegacyRoutePath): boolean {
  if (!location.lat || !location.long || path.length === 0) return false;
  const nearest = legacyFindNearest([location.lat, location.long], path);
  if (!nearest) return false;
  return (
    legacyHaversineDistance(location.lat, location.long, nearest[1], nearest[0]) >= 100
  );
}

/** `DD.MM.YY` — the map headers' "Обновление" stamp date. */
export function legacyShortDate(d: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)}`;
}

/** `HH:mm` — the map headers' "Обновление" stamp time. */
export function legacyShortTime(d: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * The debt countdown shown above the "Статус товара" label — the source's two
 * mutually-exclusive `TimeCountdown` blocks in `OrderOtherInfo`:
 *
 * - courier payment status 6 (debt), stage ≤ 15, with a handed-over pickup:
 *   a LIVE red countdown to 3 hours after the hand-over.
 * - status 7 (debt under review), same gates plus a `courier_paid_at`:
 *   a STATIC diff between hand-over and payment, green when the courier paid
 *   within 300 minutes, red past that.
 *
 * Invalid timestamps degrade to `now`, exactly as the source's
 * `dayjs(...).isValid() ? … : new Date()` did.
 */
export type LegacyDebtCountdown =
  | { kind: 'debt'; endTime: Date }
  | { kind: 'debt-review'; endTime: Date; doneAt: Date; color: 'green' | 'red' };

export function legacyDebtCountdown(
  order: LegacyOrderDetail,
  now: Date = new Date(),
): LegacyDebtCountdown | null {
  const handedOverAt = order.origin.find((leg) => leg.handed_over_at)?.handed_over_at;
  if (!handedOverAt || order.stage > 15) return null;

  const handedOver = new Date(handedOverAt);
  const validHandedOver = !Number.isNaN(handedOver.getTime());

  if (order.courier_payment_status === 6) {
    return {
      kind: 'debt',
      endTime: validHandedOver
        ? new Date(handedOver.getTime() + 3 * 60 * 60 * 1000)
        : now,
    };
  }

  if (order.courier_payment_status === 7 && order.courier_paid_at) {
    const paidAt = new Date(order.courier_paid_at);
    const validPaidAt = !Number.isNaN(paidAt.getTime());
    // `dayjs(a).diff(b, 'minutes')` truncates toward zero.
    const diffMinutes = Math.trunc((paidAt.getTime() - handedOver.getTime()) / 60_000);
    return {
      kind: 'debt-review',
      endTime: validPaidAt ? paidAt : now,
      doneAt: validHandedOver ? handedOver : now,
      color: diffMinutes <= 300 ? 'green' : 'red',
    };
  }

  return null;
}
